// ------------------------------------------------------------------------------
// <copyright file="ManualQaWorkspace.jsx" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  AlertTriangle,
  Bug,
  CheckCircle,
  ClipboardList,
  Search,
  ShieldAlert,
  Smartphone,
} from 'lucide-react';
import homePageV2Plan from '../data/manualQaHomePageV2TestPlan.json';
import iosViewPlan from '../data/manualQaIosViewTestPlan.json';
import iosBillingPlan from '../data/manualQaIosBillingTestPlan.json';
import qolsyncExploratoryPlan from '../data/manualQaQolsyncExploratoryTestPlan.json';
import qolsyncPhase2T1Plan from '../data/manualQaQolsyncPhase2T1TestPlan.json';
import qolsyncPhase2T2Plan from '../data/manualQaQolsyncPhase2T2TestPlan.json';
import { trackException } from '../services/appInsightsService';
import manualQaApiService from '../services/manualQaApiService';
import {
  clearManualQaRunState,
  loadManualQaRunState,
  saveManualQaRunState,
  updateManualQaViewResult,
} from '../utils/manualQaRunStorage';
import './ManualQaWorkspace.css';

const STATUS_OPTIONS = ['Not run', 'Pass', 'Bug', 'Blocked', 'N/A'];

const DEFAULT_CATALOG_ID = 'iosclient-view-plan-v1';

// Only the three QolSync beta catalogs are offered to testers. The generated
// iOSClient view plan and the HomePage V2 web plan stay registered in the Test
// Admin workspace so they can still be assigned as a campaign, but a beta
// tester picking from this list should see their own and nothing else.
const HIDDEN_FROM_RUNNER = [
  {
    id: DEFAULT_CATALOG_ID,
    label: 'iOSClient TestFlight',
    plan: { catalogId: DEFAULT_CATALOG_ID, ...iosViewPlan },
    eyebrow: 'Manual QA / iOSClient / Localhost Runner',
    heading: 'iOSClient View Test Plan',
    description: 'View-by-view TestFlight checklist generated from src/iOSClient. Select Bug to open repro fields before submission.',
    metricLabel: 'iOS views',
  },
  {
    id: homePageV2Plan.catalogId,
    label: 'HomePage V2 Web QA',
    plan: homePageV2Plan,
    eyebrow: homePageV2Plan.eyebrow,
    heading: homePageV2Plan.title,
    description: homePageV2Plan.description,
    metricLabel: homePageV2Plan.metricLabel,
  },
  {
    id: iosBillingPlan.catalogId,
    label: 'iOS Billing (StoreKit sandbox)',
    plan: iosBillingPlan,
    eyebrow: iosBillingPlan.eyebrow,
    heading: iosBillingPlan.title,
    description: iosBillingPlan.description,
    metricLabel: iosBillingPlan.metricLabel,
  },
];

const MANUAL_QA_CATALOGS = [
  {
    id: qolsyncExploratoryPlan.catalogId,
    label: 'QolSync Beta — Phase 1 (free testing)',
    plan: qolsyncExploratoryPlan,
    eyebrow: qolsyncExploratoryPlan.eyebrow,
    heading: qolsyncExploratoryPlan.title,
    description: qolsyncExploratoryPlan.description,
    metricLabel: qolsyncExploratoryPlan.metricLabel,
  },
  {
    id: qolsyncPhase2T1Plan.catalogId,
    label: 'QolSync Beta — Phase 2, Tester 1',
    plan: qolsyncPhase2T1Plan,
    eyebrow: qolsyncPhase2T1Plan.eyebrow,
    heading: qolsyncPhase2T1Plan.title,
    description: qolsyncPhase2T1Plan.description,
    metricLabel: qolsyncPhase2T1Plan.metricLabel,
  },
  {
    id: qolsyncPhase2T2Plan.catalogId,
    label: 'QolSync Beta — Phase 2, Tester 2',
    plan: qolsyncPhase2T2Plan,
    eyebrow: qolsyncPhase2T2Plan.eyebrow,
    heading: qolsyncPhase2T2Plan.title,
    description: qolsyncPhase2T2Plan.description,
    metricLabel: qolsyncPhase2T2Plan.metricLabel,
  },
];

const AREA_LABELS = {
  CaptureTranslate: 'Photo & Menu Translate',
  ChooseLanguage: 'Language Selection',
  ContentLibrary: 'Content Library',
  Conversation: 'Two-Way Conversation',
  'Conversation / Layouts': 'Two-Way Conversation / Layouts',
  PersonalV2: 'Translate Around Me',
  'PersonalV2 / Wizard': 'Translate Around Me / Wizard',
  RoleSelection: 'New Session',
  SessionCompletion: 'Session Completion',
  SignIn: 'Sign In',
  Speaker: 'Host / Listener',
  TranscribeOnly: 'Transcribe',
  TranslateComposer: 'Translate',
  'HomePage V2 / Accessibility': 'HomePage V2 / Accessibility',
  'HomePage V2 / Analytics': 'HomePage V2 / Analytics',
  'HomePage V2 / Guides': 'HomePage V2 / Guides',
  'HomePage V2 / Homepage': 'HomePage V2 / Homepage',
  'HomePage V2 / i18n & RTL': 'HomePage V2 / i18n & RTL',
  'HomePage V2 / Responsive': 'HomePage V2 / Responsive',
  'HomePage V2 / Routes': 'HomePage V2 / Routes',
  'HomePage V2 / Support & Legal': 'HomePage V2 / Support & Legal',
};

const SCENARIO_LABELS = {
  'ios-ai-summary-qa': 'AI Summary & Q&A',
  'ios-auth-onboarding': 'Sign In & Onboarding',
  'ios-photo-menu-translate': 'Photo & Menu Translate',
  'ios-pim-direct-soniox': 'Translate Around Me',
  'ios-plan-entitlement': 'Plan & Entitlements',
  'ios-read-transcribed-translated-text': 'Read Transcript / Translation',
  'ios-recents-delete-tags-filter-search': 'Recents',
  'ios-settings-accessibility-resilience': 'Settings & Accessibility',
  'ios-speaker-host-listener': 'Host / Listener',
  'ios-transcribe-only': 'Transcribe',
  'ios-translate-pdf-word-link-text': 'Translate: PDF, Word, Link',
  'ios-two-way-conversation': 'Two-Way Conversation',
  'homepage-v2-accessibility': 'Accessibility',
  'homepage-v2-analytics': 'Analytics & Consent',
  'homepage-v2-guides': 'Guides',
  'homepage-v2-homepage-composition': 'Homepage Composition',
  'homepage-v2-i18n-rtl': 'i18n & RTL',
  'homepage-v2-responsive': 'Responsive Browser Matrix',
  'homepage-v2-route-ownership': 'Route Ownership',
  'homepage-v2-support-legal': 'Support, Legal, Status & Contact',
};

const TESTER_TERM_REPLACEMENTS = [
  [/Test direct Soniox and, where assigned, server-routed Mode B without mixing transport route with product scenario\./g, 'Test the assigned Translate Around Me path, including Lecture and Talk where applicable.'],
  [/Translate Around Me\/PIM V2/g, 'Translate Around Me'],
  [/Start PIM session/g, 'Start listening'],
  [/Mode A or Mode B intent/g, 'Lecture or Talk intent'],
  [/server-routed Mode B/g, 'assigned server route'],
  [/direct Soniox/g, 'assigned direct route'],
  [/PIM V2/g, 'Translate Around Me'],
  [/\bPIM\b/g, 'Translate Around Me'],
  [/Personal\s*V2/g, 'Translate Around Me'],
  [/Step(\d+)/g, 'Step $1'],
];

function toTesterLabel(value) {
  if (typeof value !== 'string') {
    return value;
  }

  return TESTER_TERM_REPLACEMENTS.reduce(
    (label, [pattern, replacement]) => label.replace(pattern, replacement),
    value
  );
}

function toReadableFallbackLabel(value) {
  return toTesterLabel(value)
    .replace(/^ios-/i, '')
    .replace(/-/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, letter => letter.toUpperCase())
    .replace(/\bQa\b/g, 'QA')
    .replace(/\bIos\b/g, 'iOS');
}

function getAreaLabel(area) {
  return AREA_LABELS[area] || toReadableFallbackLabel(area);
}

function getScenarioLabel(scenarioId) {
  return SCENARIO_LABELS[scenarioId] || toReadableFallbackLabel(scenarioId);
}

function getCatalogDefinition(catalogId) {
  // Resolution still covers the hidden catalogs, so a tester assigned one from
  // the Test Admin workspace can still open it -- it just isn't in the picker.
  return MANUAL_QA_CATALOGS.concat(HIDDEN_FROM_RUNNER)
    .find(catalog => catalog.id === catalogId) || MANUAL_QA_CATALOGS[0];
}

// A tester is sent a link for their own catalog: /manual-qa?catalog=<catalogId>.
// The parameter only chooses which catalog opens -- the picker still works, so
// anyone who finishes early can switch to another. An unknown or absent value
// falls back to whatever they were last working on.
function getCatalogIdFromUrl() {
  try {
    const requested = new URLSearchParams(window.location.search).get('catalog');
    if (!requested) return '';
    const known = MANUAL_QA_CATALOGS.concat(HIDDEN_FROM_RUNNER)
      .some(catalog => catalog.id === requested);
    return known ? requested : '';
  } catch (error) {
    return '';
  }
}

// The assignment ID is what the server authorizes tester writes against, so it has to be
// the ID Campaign Builder wrote -- not one derived from this browser's runId, which the
// admin cannot know. Always recompute it: a tester carrying a stale localStorage draft
// from the old runId-derived form is repaired on their next interaction.
function withCatalogState(state, catalogId) {
  const { testerUserId } = manualQaApiService.getManualQaUserContext(state);

  return {
    ...state,
    catalogId,
    assignmentId: manualQaApiService.buildManualQaAssignmentId(testerUserId, catalogId),
  };
}

function getElapsedMilliseconds(runState, now) {
  const startedAt = Number(runState?.startedAt || now);
  const submittedAt = Number(runState?.completion?.submittedAt || 0);
  const effectiveEnd = submittedAt || now;

  return Math.max(0, effectiveEnd - startedAt);
}

function formatDuration(milliseconds) {
  const totalMinutes = Math.max(0, Math.round(milliseconds / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }

  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/**
 * Test Admin / tester workspace for local Manual QA review.
 *
 * Sprint 1 is localhost-first: the generated iOS view catalog is rendered as a
 * real AdminPortal page and run progress is saved locally. Backend persistence
 * lands in later sprints behind this same state shape.
 */
function ManualQaWorkspace() {
  const [runState, setRunState] = useState(() => {
    // Keep the saved run in step with a ?catalog= link, or autosaved results
    // would be filed against whichever catalog was last open.
    const saved = loadManualQaRunState();
    // Always run it through withCatalogState, not just when ?catalog= differs: that also
    // rewrites an assignmentId left over from the old runId-derived form, so a tester who
    // completes a run without touching a card first still submits an ID the server accepts.
    return withCatalogState(saved, getCatalogIdFromUrl() || saved.catalogId);
  });
  const [activeCatalogId, setActiveCatalogId] = useState(
    () => getCatalogDefinition(getCatalogIdFromUrl() || loadManualQaRunState().catalogId).id
  );
  const [now, setNow] = useState(() => Date.now());
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [syncState, setSyncState] = useState({
    status: 'local',
    message: 'Local draft ready',
  });

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  const activeCatalog = useMemo(
    () => getCatalogDefinition(activeCatalogId),
    [activeCatalogId]
  );
  const activePlan = activeCatalog.plan;

  const elapsedLabel = useMemo(
    () => formatDuration(getElapsedMilliseconds(runState, now)),
    [now, runState]
  );

  const areas = useMemo(
    () => Object.keys(activePlan.coverage.areas)
      .sort((left, right) => getAreaLabel(left).localeCompare(getAreaLabel(right))),
    [activePlan]
  );

  const filteredViews = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return activePlan.views.filter((view) => {
      const result = runState.viewResults[view.id] || {};
      const status = result.status || 'Not run';
      const searchable = [
        view.title,
        toTesterLabel(view.title),
        view.viewName,
        view.area,
        getAreaLabel(view.area),
        view.kind,
        toTesterLabel(view.kind),
        view.path,
        view.parentScenarioId,
        getScenarioLabel(view.parentScenarioId),
        view.purpose,
        toTesterLabel(view.purpose),
        view.mockup.caption,
        toTesterLabel(view.mockup.caption),
        view.mockup.headline,
        toTesterLabel(view.mockup.headline),
        view.mockup.primary,
        toTesterLabel(view.mockup.primary),
        ...view.mockup.lines,
        ...view.mockup.lines.map(toTesterLabel),
      ].join(' ').toLowerCase();

      return (!normalizedSearch || searchable.includes(normalizedSearch))
        && (!areaFilter || view.area === areaFilter)
        && (!statusFilter || status === statusFilter);
    });
  }, [activePlan, areaFilter, runState.viewResults, searchTerm, statusFilter]);

  const summary = useMemo(() => {
    const activeViewIds = new Set(activePlan.views.map(view => view.id));
    const results = Object.entries(runState.viewResults)
      .filter(([viewId]) => activeViewIds.has(viewId))
      .map(([, result]) => result);

    return {
      pass: results.filter(result => result.status === 'Pass').length,
      bug: results.filter(result => result.status === 'Bug').length,
      blocked: results.filter(result => result.status === 'Blocked').length,
    };
  }, [activePlan, runState.viewResults]);

  const persist = useCallback((nextState, planOverride = activePlan) => {
    const planCatalogId = planOverride.catalogId || activeCatalog.id;
    const saved = saveManualQaRunState(withCatalogState(nextState, planCatalogId));
    setRunState(saved);
    setSyncState({ status: 'saving', message: 'Saving draft' });

    manualQaApiService.autosaveRun(saved, planOverride)
      .then((serverRun) => {
        setSyncState({ status: 'synced', message: 'Backend synced' });

        if (serverRun?.id) {
          const withServerRun = saveManualQaRunState({
            ...saved,
            serverRunId: serverRun.id,
            lastSyncedAt: Date.now(),
          });
          setRunState(current => (current.updatedAt === saved.updatedAt ? withServerRun : current));
        }
      })
      .catch((error) => {
        trackException(error, { errorType: 'ManualQa_AutosaveFailed' });
        // Say what actually went wrong. "API unavailable" read as a transient network blip,
        // so an authorization failure -- the tester's work never reaching Test Admin at all --
        // looked identical to a hiccup and went unnoticed for a whole run.
        setSyncState({
          status: 'not-synced',
          message: `NOT saved to server: ${error.message || 'unknown error'} - tell your beta manager`,
        });
      });

    return saved;
  }, [activeCatalog.id, activePlan]);

  const handleCatalogChange = useCallback((catalogId) => {
    const nextCatalog = getCatalogDefinition(catalogId);

    setActiveCatalogId(nextCatalog.id);
    setSearchTerm('');
    setAreaFilter('');
    setStatusFilter('');
    persist(withCatalogState(runState, nextCatalog.id), nextCatalog.plan);
  }, [persist, runState]);

  const patchView = useCallback((viewId, patch) => {
    persist(updateManualQaViewResult(runState, viewId, patch));
  }, [persist, runState]);

  const handleStepToggle = useCallback((viewId, stepIndex, checked) => {
    const result = runState.viewResults[viewId] || {};
    patchView(viewId, {
      checkedSteps: {
        ...(result.checkedSteps || {}),
        [stepIndex]: checked,
      },
    });
  }, [patchView, runState.viewResults]);

  const handleBugField = useCallback((viewId, field, value) => {
    const result = runState.viewResults[viewId] || {};
    patchView(viewId, {
      bug: {
        ...(result.bug || {}),
        [field]: value,
      },
    });
  }, [patchView, runState.viewResults]);

  const handleEvidenceFile = useCallback(async (view, file) => {
    if (!file) {
      return;
    }

    const result = runState.viewResults[view.id] || {};
    const bug = result.bug || {};

    patchView(view.id, {
      bug: {
        ...bug,
        evidenceFileName: file.name,
        evidenceStatus: 'saving',
      },
    });

    try {
      const evidence = await manualQaApiService.saveEvidenceFile(runState, {
        bugReportId: result.bugReportId || '',
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size || 0,
      }, file);

      patchView(view.id, {
        bug: {
          ...bug,
          evidenceFileName: file.name,
          evidenceId: evidence.id,
          evidenceStatus: 'saved',
        },
      });
      setSyncState({ status: 'synced', message: 'Evidence uploaded' });
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_EvidenceUploadFailed' });
      patchView(view.id, {
        bug: {
          ...bug,
          evidenceFileName: file.name,
          evidenceStatus: 'failed',
          evidenceError: error.message || 'Evidence upload failed',
        },
      });
      setSyncState({
        status: 'not-synced',
        message: `Screenshot NOT uploaded: ${error.message || 'unknown error'}`,
      });
    }
  }, [patchView, runState]);

  const handleClearLocalDraft = useCallback(() => {
    clearManualQaRunState();
    setRunState(withCatalogState(loadManualQaRunState(), activeCatalog.id));
    setSyncState({ status: 'local', message: 'Local draft ready' });
  }, [activeCatalog.id]);

  const handleCompletionHours = useCallback((value) => {
    const testerReportedHours = Number(value);
    persist({
      ...runState,
      completion: {
        ...(runState.completion || {}),
        testerReportedHours: Number.isFinite(testerReportedHours) ? testerReportedHours : 0,
      },
    });
  }, [persist, runState]);

  const handleSubmitBug = useCallback(async (view) => {
    const result = runState.viewResults[view.id] || {};

    try {
      setSyncState({ status: 'saving', message: 'Submitting bug' });
      const submittedBug = await manualQaApiService.submitBug(runState, view, result);
      const nextState = updateManualQaViewResult(runState, view.id, {
        bugReportId: submittedBug.id,
        bug: {
          ...(result.bug || {}),
          submissionStatus: 'submitted',
          githubIssueState: submittedBug.githubIssueState || 'pending',
        },
      });

      persist(nextState);
      setSyncState({ status: 'synced', message: 'Bug submitted' });
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_BugSubmitFailed' });
      patchView(view.id, {
        bug: {
          ...(result.bug || {}),
          submissionStatus: 'failed',
          submissionError: error.message || 'Bug submit failed',
        },
      });
    }
  }, [patchView, persist, runState]);

  // Cards routinely turn up more than one problem. Without this the form still held the
  // submitted bug's ID, so a second report updated the first instead of adding to it.
  const handleReportAnotherBug = useCallback((view) => {
    patchView(view.id, {
      bugReportId: '',
      bug: { severity: (runState.viewResults[view.id]?.bug || {}).severity || 'High' },
    });
  }, [patchView, runState.viewResults]);

  const handleCompleteRun = useCallback(async () => {
    try {
      setSyncState({ status: 'saving', message: 'Submitting completion report' });
      const submittedAt = Date.now();
      const runForSubmission = saveManualQaRunState({
        ...runState,
        updatedAt: submittedAt,
        completion: {
          ...(runState.completion || {}),
          status: 'submitting',
          submittedAt,
        },
      });

      setNow(submittedAt);
      setRunState(runForSubmission);

      const completionReport = await manualQaApiService.completeRun(runForSubmission, activePlan);
      const completionReportId = completionReport.completionReport?.id
        || completionReport.id
        || completionReport.runId
        || runForSubmission.runId;
      const hoursReviewId = completionReport.hoursReview?.id
        || completionReport.hoursReviewId
        || '';
      const saved = saveManualQaRunState({
        ...runForSubmission,
        completion: {
          ...(runForSubmission.completion || {}),
          status: 'submitted',
          submittedAt,
          completionReportId,
          hoursReviewId,
        },
      });

      setRunState(saved);
      setSyncState({ status: 'synced', message: 'Completion report submitted' });
    } catch (error) {
      trackException(error, { errorType: 'ManualQa_CompletionSubmitFailed' });
      setSyncState({ status: 'local-fallback', message: 'Completion saved locally - API unavailable' });
    }
  }, [activePlan, runState]);

  return (
    <div className="mq-page">
      <header className="mq-header">
        <div>
          <p className="mq-eyebrow">{activeCatalog.eyebrow}</p>
          <h1>{activeCatalog.heading}</h1>
          <p className="mq-subtitle">
            {activeCatalog.description}
          </p>
          {(activePlan.catalogVersion || activePlan.generatedAt) && (
            <p className="mq-catalog-provenance">
              {activePlan.catalogVersion ? <>Catalog <code>{activePlan.catalogVersion}</code></> : null}
              {activePlan.generatedAt ? <> · generated {new Date(activePlan.generatedAt).toISOString().slice(0, 10)} UTC</> : null}
              {activePlan.generatedFrom ? <> from <code>{activePlan.generatedFrom}</code></> : null}
            </p>
          )}
        </div>
        <div className="mq-header-actions">
          <span className={`mq-sync-state mq-sync-${syncState.status}`}>{syncState.message}</span>
          <span className="mq-duration-state" aria-label={`System tracked time ${elapsedLabel}`}>
            <span>System tracked</span>
            <strong>{elapsedLabel}</strong>
          </span>
          <span className="mq-save-state">Saved {new Date(runState.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          <button className="mq-secondary-button" type="button" onClick={handleClearLocalDraft}>
            Clear local draft
          </button>
        </div>
      </header>

      <section className="mq-metrics" aria-label="Manual QA progress">
        <Metric icon={Smartphone} value={activePlan.coverage.totalViews} label={activeCatalog.metricLabel} />
        <Metric icon={CheckCircle} value={summary.pass} label="passed" />
        <Metric icon={Bug} value={summary.bug} label="bugs" />
        <Metric icon={ShieldAlert} value={summary.blocked} label="blocked" />
      </section>

      <section className="mq-controls" aria-label="Manual QA filters">
        <label>
          <span>Catalog</span>
          <select value={activeCatalog.id} onChange={(event) => handleCatalogChange(event.target.value)}>
            {MANUAL_QA_CATALOGS.map(catalog => (
              <option key={catalog.id} value={catalog.id}>{catalog.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Search</span>
          <div className="mq-search-field">
            <Search size={16} />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search view, area, source path, or scenario"
            />
          </div>
        </label>
        <label>
          <span>Area</span>
          <select value={areaFilter} onChange={(event) => setAreaFilter(event.target.value)}>
            <option value="">All areas</option>
            {areas.map(area => (
              <option key={area} value={area}>{getAreaLabel(area)} ({activePlan.coverage.areas[area]})</option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </label>
      </section>

      <section className="mq-guidance">
        <div>
          <ClipboardList size={18} />
          <strong>How to test</strong>
          <span>Reach the view in TestFlight, execute the steps, attach evidence, then mark status.</span>
        </div>
        <div>
          <AlertTriangle size={18} />
          <strong>Bug workflow</strong>
          <span>Bug status opens expected, actual, repro, severity, and screenshot fields inline.</span>
        </div>
      </section>

      <section className="mq-completion-panel" aria-label="Complete Manual QA run">
        <div className="mq-completion-copy">
          <strong>Complete at the end of the testing session</strong>
          <span>System time is tracked automatically. Tester hours are optional and reviewed against the system log.</span>
          <dl>
            <div>
              <dt>System tracked</dt>
              <dd>{elapsedLabel}</dd>
            </div>
            <div>
              <dt>Started</dt>
              <dd>{new Date(runState.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</dd>
            </div>
            <div>
              <dt>Last saved</dt>
              <dd>{new Date(runState.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</dd>
            </div>
          </dl>
        </div>
        <div className="mq-completion-actions">
          <label className="mq-hours-input">
            <span>Tester hours optional</span>
            <input
              aria-label="Tester hours optional"
              type="number"
              min="0"
              step="0.25"
              value={runState.completion?.testerReportedHours || 0}
              onChange={(event) => handleCompletionHours(event.target.value)}
            />
          </label>
          <button className="mq-primary-button" type="button" onClick={handleCompleteRun}>
            Complete run
          </button>
        </div>
      </section>

      <section className="mq-view-list" aria-label={`${activeCatalog.label} checklist`}>
        {filteredViews.map(view => (
          <ViewTestCard
            key={view.id}
            view={view}
            result={runState.viewResults[view.id] || {}}
            onStatusChange={(status) => patchView(view.id, { status })}
            onStepToggle={(stepIndex, checked) => handleStepToggle(view.id, stepIndex, checked)}
            onBugField={(field, value) => handleBugField(view.id, field, value)}
            onEvidenceFile={(file) => handleEvidenceFile(view, file)}
            onSubmitBug={() => handleSubmitBug(view)}
            onReportAnotherBug={() => handleReportAnotherBug(view)}
          />
        ))}
        {filteredViews.length === 0 && (
          <div className="mq-empty">
            <h2>No matching views</h2>
            <p>Adjust search, area, or status filters.</p>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Metric card for the Manual QA progress header.
 *
 * @param {object} props
 * @param {React.ComponentType} props.icon - Icon component.
 * @param {number} props.value - Metric value.
 * @param {string} props.label - Metric label.
 */
function Metric({ icon: Icon, value, label }) {
  return (
    <div className="mq-metric">
      <Icon size={18} />
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

/**
 * One view-level tester card.
 *
 * @param {object} props
 * @param {object} props.view - View plan entry.
 * @param {object} props.result - Saved view result.
 * @param {Function} props.onStatusChange - Status callback.
 * @param {Function} props.onStepToggle - Step checkbox callback.
 * @param {Function} props.onBugField - Bug field callback.
 * @param {Function} props.onEvidenceFile - Evidence file callback.
 * @param {Function} props.onSubmitBug - Bug submission callback.
 * @param {Function} props.onReportAnotherBug - Clears the form for a second finding on this card.
 */
function ViewTestCard({ view, result, onStatusChange, onStepToggle, onBugField, onEvidenceFile, onSubmitBug, onReportAnotherBug }) {
  const status = result.status || 'Not run';
  const bug = result.bug || {};
  const checkedSteps = result.checkedSteps || {};
  const canSubmitBug = Boolean((bug.summary || '').trim() && (bug.reproSteps || '').trim());
  const areaLabel = getAreaLabel(view.area);
  const kindLabel = toTesterLabel(view.kind);
  const scenarioLabel = getScenarioLabel(view.parentScenarioId);
  const title = toTesterLabel(view.title);
  const mockupCaption = toTesterLabel(view.mockup.caption);
  const mockupHeadline = toTesterLabel(view.mockup.headline);
  const mockupPrimary = toTesterLabel(view.mockup.primary);
  const mockupLines = view.mockup.lines.map(toTesterLabel);

  return (
    <article className="mq-view-card" data-testid={`manual-qa-view-${view.id}`}>
      <div className="mq-phone" aria-hidden="true">
        <div className="mq-phone-top" />
        <div className="mq-phone-body">
          <span>{mockupCaption}</span>
          <strong>{mockupHeadline}</strong>
          {mockupLines.map((line, index) => <p key={`${line}-${index}`}>{line}</p>)}
          <button type="button" tabIndex={-1}>{mockupPrimary}</button>
        </div>
      </div>

      <div className="mq-view-body">
        <div className="mq-tags">
          <span>{areaLabel}</span>
          <span>{kindLabel}</span>
          <span>{scenarioLabel}</span>
        </div>
        <h2>{title}</h2>
        <code>{view.path}:{view.line}</code>
        <p><strong>Purpose:</strong> {toTesterLabel(view.purpose)}</p>

        <div className="mq-steps">
          <strong>How to test</strong>
          <ol className="mq-step-list">
            {view.howToTest.map((step, index) => (
              <li
                key={step}
                className="mq-step-row"
                data-testid={`manual-qa-step-${view.id}-${index}`}
              >
                <span className="mq-step-number" aria-hidden="true">{index + 1}.</span>
                <label className="mq-step-check">
                  <input
                    type="checkbox"
                    checked={Boolean(checkedSteps[index])}
                    onChange={(event) => onStepToggle(index, event.target.checked)}
                  />
                  <span className="mq-step-text">{toTesterLabel(step)}</span>
                </label>
              </li>
            ))}
          </ol>
        </div>

        <fieldset className="mq-status-group">
          <legend>Status</legend>
          {STATUS_OPTIONS.map(option => (
            <label key={option} className={`mq-status-choice mq-status-${option.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <input
                type="radio"
                name={`status-${view.id}`}
                value={option}
                checked={status === option}
                onChange={() => onStatusChange(option)}
              />
              {option}
            </label>
          ))}
        </fieldset>

        {status === 'Bug' && (
          <section className="mq-bug-panel" aria-label={`Bug report for ${title}`}>
            <h3>Bug report</h3>
            <div className="mq-bug-grid">
              <label>
                <span>Severity</span>
                <select value={bug.severity || 'High'} onChange={(event) => onBugField('severity', event.target.value)}>
                  <option>Blocker</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </label>
              <label>
                <span>Screenshot or recording</span>
                <input type="file" onChange={(event) => onEvidenceFile(event.target.files?.[0])} />
              </label>
            </div>
            {bug.evidenceStatus === 'saved' && (
              <span className="mq-inline-success">Evidence uploaded</span>
            )}
            {bug.evidenceStatus === 'failed' && (
              <span className="mq-inline-error">{bug.evidenceError}</span>
            )}
            <label>
              <span>Bug summary</span>
              <input value={bug.summary || ''} onChange={(event) => onBugField('summary', event.target.value)} placeholder="Short title Test Admin can triage" />
            </label>
            <label>
              <span>Expected result</span>
              <textarea value={bug.expected || ''} onChange={(event) => onBugField('expected', event.target.value)} rows="2" />
            </label>
            <label>
              <span>Actual result</span>
              <textarea value={bug.actual || ''} onChange={(event) => onBugField('actual', event.target.value)} rows="2" />
            </label>
            <label>
              <span>Repro steps</span>
              <textarea value={bug.reproSteps || ''} onChange={(event) => onBugField('reproSteps', event.target.value)} rows="4" />
            </label>
            <div className="mq-bug-actions">
              <button
                className="mq-primary-button"
                type="button"
                disabled={!canSubmitBug}
                onClick={onSubmitBug}
              >
                Submit bug
              </button>
              {bug.submissionStatus === 'submitted' && (
                <>
                  <span className="mq-inline-success">Bug submitted</span>
                  <button
                    className="mq-secondary-button"
                    type="button"
                    onClick={onReportAnotherBug}
                  >
                    Report another bug on this card
                  </button>
                </>
              )}
              {bug.submissionStatus === 'failed' && (
                <span className="mq-inline-error">{bug.submissionError || 'Bug submit failed'}</span>
              )}
            </div>
            <p className="mq-bug-note">
              Bug submissions save to Manual QA first. Test Admin can create the GitHub issue server-side after triage.
            </p>
          </section>
        )}
      </div>
    </article>
  );
}

export default ManualQaWorkspace;
